import React, { useState } from 'react';
import { Plus, Search, Edit2, Trash2, CreditCard } from 'lucide-react';
import { apiService } from '../services/api';
import { Tariff } from '../types';
import { showGlobalNotification } from '../hooks/useNotifications';
import { useLanguage } from '../contexts/LanguageContext';

const TariffsPage: React.FC = () => {
  const { t } = useLanguage();
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingTariff, setEditingTariff] = useState<Tariff | null>(null);
  const [tariffsData, setTariffsData] = useState<Tariff[]>([]);
  const [tariffUsage, setTariffUsage] = useState<{[key: number]: number}>({});
  const [loading, setLoading] = useState(true);
  const [tariffData, setTariffData] = useState({
    name: '',
    price: 0,
    description: ''
  });

  // Load data on component mount
  React.useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [tariffsResponse, gsmLinesResponse] = await Promise.all([
        apiService.getTariffs(),
        apiService.getGSMLines()
      ]);

      const safeTariffsResponse = Array.isArray(tariffsResponse) ? tariffsResponse : [];
      const safeGsmLinesResponse = Array.isArray(gsmLinesResponse) ? gsmLinesResponse : [];

      setTariffsData(safeTariffsResponse);

      // Her tarife için kullanım sayısını hesapla
      const usage: {[key: number]: number} = {};
      safeTariffsResponse.forEach((tariff: Tariff) => {
        usage[tariff.id] = safeGsmLinesResponse.filter((line: any) => line.tariff_id === tariff.id).length;
      });
      setTariffUsage(usage);
    } catch (error) {
      console.error('Error loading data:', error);
      showGlobalNotification.error(t('common.error'), t('tariffs.errorLoadData'));
    } finally {
      setLoading(false);
    }
  };

  const filteredTariffs = tariffsData.filter(tariff =>
    String(tariff.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    String(tariff.description || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleEdit = (tariff: Tariff) => {
    setEditingTariff(tariff);
    setTariffData({
      name: tariff.name,
      price: tariff.price,
      description: tariff.description || ''
    });
    setShowModal(true);
  };

  const handleAdd = () => {
    setEditingTariff(null);
    setTariffData({
      name: '',
      price: 0,
      description: ''
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!tariffData.name || tariffData.price <= 0) {
      showGlobalNotification.error(t('common.error'), t('tariffs.errorRequired'));
      return;
    }

    try {
      if (editingTariff) {
        // Update existing tariff
        await apiService.updateTariff(editingTariff.id, tariffData);
        showGlobalNotification.success(t('common.success'), t('tariffs.successUpdated'));
      } else {
        // Add new tariff
        await apiService.createTariff(tariffData);
        showGlobalNotification.success(t('common.success'), t('tariffs.successCreated'));
      }

      // Reload data
      await loadData();
      setShowModal(false);
      setTariffData({ name: '', price: 0, description: '' });
    } catch (error: any) {
      console.error('Error saving tariff:', error);
      showGlobalNotification.error(t('common.error'), error.message || t('tariffs.errorSave'));
    }
  };

  const handleDelete = async (id: number) => {
    const tariffToDelete = tariffsData.find(t => t.id === id);
    if (!tariffToDelete) return;

    const usageCount = tariffUsage[id] || 0;

    if (usageCount > 0) {
      showGlobalNotification.error(
        t('tariffs.deleteBlocked'),
        t('tariffs.deleteBlockedMsg', { count: usageCount })
      );
      return;
    }

    if (confirm(t('tariffs.deleteConfirm'))) {
      try {
        await apiService.deleteTariff(id);
        await loadData();
        showGlobalNotification.success(t('common.success'), t('tariffs.successDeleted'));
      } catch (error: any) {
        console.error('Error deleting tariff:', error);
        showGlobalNotification.error(t('common.error'), error.message || t('tariffs.errorDelete'));
      }
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">{t('tariffs.title')}</h1>
          <p className="text-gray-600 dark:text-gray-400 text-sm sm:text-base">{t('tariffs.subtitle')}</p>
        </div>
        <button
          onClick={handleAdd}
          className="flex items-center justify-center px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors min-h-[44px] text-sm sm:text-base w-full sm:w-auto"
        >
          <Plus className="w-4 h-4 mr-2" />
          {t('tariffs.newTariff')}
        </button>
      </div>

      {/* Search */}
      <div className="relative mb-6">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
        <input
          type="text"
          placeholder={t('tariffs.searchPlaceholder')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 pr-4 py-3 text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent w-full min-h-[44px]"
        />
      </div>

      {/* Tariffs List */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-700">
            <tr>
              <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-400">{t('tariffs.colName')}</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-400">{t('tariffs.colDescription')}</th>
              <th className="text-right px-4 py-3 font-medium text-gray-600 dark:text-gray-400">{t('tariffs.colPrice')}</th>
              <th className="text-center px-4 py-3 font-medium text-gray-600 dark:text-gray-400">{t('tariffs.colLines')}</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-400">{t('tariffs.colCreated')}</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {filteredTariffs.map((tariff) => (
              <tr key={tariff.id} className="hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700 transition-colors">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <CreditCard className="w-4 h-4 text-red-500 shrink-0" />
                    <span className="font-medium text-gray-900 dark:text-gray-100">{tariff.name}</span>
                  </div>
                </td>
                <td className="px-4 py-3 text-gray-500 dark:text-gray-400 max-w-xs truncate">
                  {tariff.description || <span className="text-gray-300">—</span>}
                </td>
                <td className="px-4 py-3 text-right font-semibold text-red-600">
                  ₺{Number(tariff.price).toFixed(2)}
                </td>
                <td className="px-4 py-3 text-center">
                  <button
                    className="text-primary hover:text-primary-dark font-medium hover:underline"
                    onClick={() => {
                      const event = new CustomEvent('navigate', { detail: 'gsm-lines' });
                      window.dispatchEvent(event);
                      localStorage.setItem('gsmLinesFilter', JSON.stringify({
                        tariff_id: tariff.id,
                        tariff_name: tariff.name
                      }));
                    }}
                    title={t('tariffs.linesTitle')}
                  >
                    {t('tariffs.linesCount', { count: tariffUsage[tariff.id] || 0 })}
                  </button>
                </td>
                <td className="px-4 py-3 text-gray-400 text-xs whitespace-nowrap">
                  {new Date(tariff.created_at).toLocaleDateString('tr-TR')}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-1">
                    <button
                      onClick={() => handleEdit(tariff)}
                      className="p-1.5 text-gray-400 hover:text-primary transition-colors rounded"
                      title={t('tariffs.editTitle')}
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(tariff.id)}
                      className={`p-1.5 transition-colors rounded ${
                        (tariffUsage[tariff.id] || 0) > 0
                          ? 'text-gray-200 cursor-not-allowed'
                          : 'text-gray-400 hover:text-red-600'
                      }`}
                      title={(tariffUsage[tariff.id] || 0) > 0 ? t('tariffs.deleteDisabledTitle') : t('tariffs.deleteTitle')}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {filteredTariffs.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-10 text-center text-gray-400">
                  {t('tariffs.notFound')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-md">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {editingTariff ? t('tariffs.modalEditTitle') : t('tariffs.modalNewTitle')}
              </h2>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('tariffs.labelName')}
                </label>
                <input
                  type="text"
                  value={tariffData.name}
                  onChange={(e) => setTariffData({...tariffData, name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  placeholder={t('tariffs.placeholderName')}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('tariffs.labelPrice')}
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={tariffData.price}
                  onChange={(e) => setTariffData({...tariffData, price: parseFloat(e.target.value) || 0})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  placeholder={t('tariffs.placeholderPrice')}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('tariffs.labelDescription')}
                </label>
                <textarea
                  value={tariffData.description}
                  onChange={(e) => setTariffData({...tariffData, description: e.target.value})}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  placeholder={t('tariffs.placeholderDescription')}
                />
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-3">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700 transition-colors"
              >
                {t('tariffs.cancel')}
              </button>
              <button
                onClick={handleSave}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
              >
                {editingTariff ? t('tariffs.update') : t('tariffs.save')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TariffsPage;
